import type { HookAPI } from 'antd/es/modal/useModal'
import { type MutableRefObject, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { toast } from 'sonner'

import type { AtomModalApi } from '@/components/Atoms/AtomModal/useAtomModal'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import { STOCK_IMPORT_LIVE_CONFIRMATION } from '../../lib/stockImportsMessages'
import type {
  StockImportConfigModel,
  StockImportConfigRequest,
  StockImportConfigShortModel,
  StockImportRunMode,
  StoreModel,
} from '../../types/stockImports.types'
import {
  type ConfigFormInput,
  configFormResolverSchema,
  type ConfigFormValues,
} from '../lib/configFormModel'
import { buildConfigRequest } from '../lib/configRequest'
import { useConfigPersistence } from './useConfigPersistence'
import type { ConfigPriceFormApi } from './useConfigPriceForm'

type ConfigActionsProps = {
  config: StockImportConfigModel | null
  stores: StoreModel[]
  modal: HookAPI
  onClose: () => void
  onSaved: (config: StockImportConfigModel) => void
  onPersisted: (config: StockImportConfigShortModel) => void
  onRun: (
    config: StockImportConfigShortModel,
    mode: StockImportRunMode
  ) => Promise<boolean>
}

type UseConfigActionsParams = {
  props: ConfigActionsProps
  form: UseFormReturn<ConfigFormInput, unknown, ConfigFormValues>
  atomModal: AtomModalApi
  isSelfSupplierMode: boolean
  isSourceValidated: boolean
  formVersionRef: MutableRefObject<number>
  rejectMaskedPassword: (values: ConfigFormInput) => boolean
  prices: Pick<ConfigPriceFormApi, 'buildSettings' | 'markClean' | 'isDirty'>
}

export const useConfigActions = ({
  props,
  form,
  atomModal,
  isSelfSupplierMode,
  isSourceValidated,
  formVersionRef,
  rejectMaskedPassword,
  prices,
}: UseConfigActionsParams) => {
  const { config, stores, modal } = props
  const [saving, setSaving] = useState(false)
  const [trialLoading, setTrialLoading] = useState(false)
  const [runLoading, setRunLoading] = useState(false)
  const persistence = useConfigPersistence({
    config,
    isSelfSupplierMode,
    atomModal,
    onPersisted: props.onPersisted,
  })

  const resolveSelfSupplierId = (storeId: string) =>
    stores.find((store) => store.id === storeId)?.productSupplierShortDto?.id ??
    (config?.store?.id === storeId ? config?.productSupplier?.id : undefined) ??
    null

  const buildMainPayload =
    async (): Promise<StockImportConfigRequest | null> => {
      if (!(await form.trigger())) {
        handleFormSubmitError(false)
        return null
      }
      const values = form.getValues()
      const parsed = configFormResolverSchema.safeParse(values)
      if (!parsed.success || rejectMaskedPassword(values)) return null
      const supplierId = isSelfSupplierMode
        ? resolveSelfSupplierId(parsed.data.storeId)
        : parsed.data.supplierId
      return buildConfigRequest(supplierId, parsed.data, 'DRAFT')
    }

  const buildPayload = async (): Promise<StockImportConfigRequest | null> => {
    const payload = await buildMainPayload()
    if (!payload) return null
    const priceSettings = await prices.buildSettings()
    if (priceSettings === null) return null
    return priceSettings ? { ...payload, priceSettings } : payload
  }

  const persistAndKeepClean = async (payload: StockImportConfigRequest) => {
    const versionAtRequest = formVersionRef.current
    const result = await persistence.persist(payload)
    if (
      result.outcome !== 'adopted-kept' &&
      formVersionRef.current === versionAtRequest
    ) {
      form.reset(form.getValues())
      prices.markClean()
    }
    return result
  }

  const save = async () => {
    if (!isSourceValidated) {
      toast.error('Проверьте источник перед сохранением')
      return
    }
    setSaving(true)
    const toastId = toast.loading(
      persistence.persistedConfig
        ? 'Сохранение настройки...'
        : 'Создание настройки...'
    )
    try {
      const payload = await buildPayload()
      if (!payload) {
        toast.dismiss(toastId)
        return
      }
      const result = await persistAndKeepClean(payload)
      if (result.outcome === 'adopted-kept') {
        toast.success(
          'Существующая настройка этого склада оставлена без изменений',
          { id: toastId }
        )
        return
      }
      toast.success(
        result.outcome === 'created'
          ? 'Настройка создана как черновик'
          : 'Настройка сохранена как черновик. Для активации выполните загрузку с сохранением',
        { id: toastId }
      )
      props.onSaved(result.config)
    } catch (error) {
      toast.dismiss(toastId)
      handleApiError(error, modal)
    } finally {
      setSaving(false)
    }
  }

  const saveAndRun = async (mode: StockImportRunMode) => {
    const isTrial = mode === 'trial'
    if (!isSourceValidated) {
      toast.error(
        isTrial
          ? 'Проверьте источник перед пробным запуском'
          : 'Проверьте источник перед запуском с сохранением'
      )
      return
    }
    const setLoading = isTrial ? setTrialLoading : setRunLoading
    setLoading(true)
    try {
      let targetConfig: StockImportConfigShortModel | null =
        persistence.persistedConfig
      let savedNow = false
      if (!targetConfig || form.formState.isDirty || prices.isDirty) {
        const payload = await buildPayload()
        if (!payload) return
        const result = await persistAndKeepClean(payload)
        if (result.outcome === 'adopted-kept') {
          toast.error(
            'У этого склада уже есть настройка загрузки — запуск отменён, существующая настройка оставлена без изменений'
          )
          return
        }
        targetConfig = result.config
        savedNow = true
      }
      if (await props.onRun(targetConfig, mode)) {
        props.onClose()
        return
      }
      if (savedNow)
        toast.info(
          'Настройка сохранена как черновик и уже есть в списке настроек'
        )
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }

  const confirmLiveRun = () => {
    atomModal.confirm({
      ...STOCK_IMPORT_LIVE_CONFIRMATION,
      onOk: () => {
        void saveAndRun('live')
      },
    })
  }

  const runPreview = async (
    loadPreview: (
      payload: StockImportConfigRequest,
      configId: string | null
    ) => Promise<void>
  ) => {
    if (!isSourceValidated) {
      toast.error('Сначала проверьте источник на первом шаге')
      return
    }
    const payload = await buildPayload()
    if (!payload) return
    await loadPreview(payload, persistence.persistedConfig?.id ?? null)
  }

  return {
    saving,
    trialLoading,
    runLoading,
    savedConfig: persistence.savedConfig,
    persistedConfig: persistence.persistedConfig,
    buildMainPayload,
    buildPayload,
    save,
    saveAndRun,
    confirmLiveRun,
    runPreview,
  }
}
